import { placeTabIndicator } from './tabIndicator'

// The two chrome navs' half of the sliding highlight — the top nav and the
// phone tab bar, which are the same group under different layouts and share
// this file rather than each having their own idea of what a nav is.
// Everything about *placing* the indicator lives in lib/tabIndicator.ts,
// shared with components/ui/TabGroup; what is here is the two things only
// these groups need.
//
// One: which link is active is decided at runtime rather than rendered. Both
// nav elements are data-turbo-permanent — they have to be, or the indicator
// would be thrown away and rebuilt on every navigation and would have nowhere
// to slide from — so the markup Turbo carries across is whatever the *first*
// page in the session rendered. Reading window.location here is what keeps it
// honest.
//
// Two: this indicator never runs a CSS transition. The slide across a
// navigation belongs to the browser. The pill carries a view-transition-name
// (see the ::view-transition rules in styles/application.css), so when Turbo
// renders inside document.startViewTransition the browser captures where the
// pill was, captures where it ends up, and animates the gap itself.
//
// That division of labour is the whole design, and it is worth restating
// because animating from here is the obvious thing to reach for and cannot be
// made to work: a transition started during the render has barely begun when
// the browser captures the incoming snapshot, so it captures a pill that has
// hardly moved and treats *that* as the destination. Write the final position
// synchronously, and let the group animation carry it there.

function activePath(link: HTMLAnchorElement): boolean {
  const prefix = link.dataset.navMatch
  if (!prefix) return false

  const path = window.location.pathname
  if (link.dataset.navExact === '1') return path === prefix

  return path === prefix || path.startsWith(`${prefix}/`)
}

// Both are laid out at every width, but only ever one at a time — the other is
// display:none — so this walks both unconditionally and the hidden one's
// indicator simply measures a zero box it will re-measure on resize.
const GROUPS = ['app-nav', 'app-tabbar']

function update() {
  GROUPS.forEach((id) => {
    const nav = document.getElementById(id)
    if (!nav) return

    nav.querySelectorAll<HTMLAnchorElement>('a[data-nav-match]').forEach((link) => {
      const on = activePath(link)
      link.dataset.tabOn = on ? '1' : '0'
      // One property for both navs: the tab bar's glyph strokes in
      // currentColor and its label inherits, so this moves the icon and the
      // word together.
      link.style.color = on ? 'var(--color-text)' : 'var(--color-muted)'
      if (on) {
        link.setAttribute('aria-current', 'page')
      } else {
        link.removeAttribute('aria-current')
      }
    })

    // 'none' rather than NAV_INDICATOR_TRANSITION: the constant documents the
    // motion the design asks for, and the ::view-transition-group rule in
    // styles/application.css is where that duration and curve are actually
    // spent. Passing it here would be the mid-flight capture described above.
    placeTabIndicator(nav, 'none')
  })
}

export function initAppNav() {
  update()

  // turbo:render, not turbo:load, is what places the pill for a navigation: it
  // fires inside the callback Turbo hands to startViewTransition, with the new
  // body in place and before the incoming snapshot is captured, so the position
  // written here is the one the browser animates towards. turbo:load is far too
  // late — Turbo awaits transition.finished first, by which point the pill has
  // already been animated to wherever it was standing.
  document.addEventListener('turbo:render', update)
  // Still worth listening for: this covers the paths that never reach a render
  // (restoring a cached page, a visit that turns out to be a no-op), and
  // re-running update on a pill that is already right is a no-op itself.
  document.addEventListener('turbo:load', update)
  // Fonts land after first paint and the brand face is wider than the fallback,
  // so without this the indicator sits under where the label *was*.
  document.fonts?.ready.then(update).catch(() => {})
  window.addEventListener('resize', update)
}
